import React, { useCallback, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Spinner } from 'reactstrap';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import ProductsSearchFilters from './ProductsSearchFilters';
import { fetchCatalogProducts } from '../../../services/Http/Api';
import { useNavigate } from 'react-router-dom';
import { MdVisibility } from 'react-icons/md';
import { useCurrency } from '../../../context/CurrencyProvider';

const ProductsOverview = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [filters, setFilters] = useState({ searchTerm: '', manufacturerId: '', categoryId: '' });
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('');

    const columns = [
        {
            name: t('Products.Overview.sku'),
            selector: row => row.organization_sku || row.variant_sku || 'N/A',
            sortable: true,
            key: 'organization_sku',
        },
        {
            name: t('Products.Overview.product_name'),
            selector: row => row.product_name || 'N/A',
            sortable: true,
            key: 'product_name',
        },
        {
            name: t('Products.Overview.manufacturer'),
            selector: row => row.manufacturer_name || 'N/A',
            sortable: true,
            key: 'manufacturer_name',
        },
        {
            name: t('Products.Overview.category'),
            selector: row => row.category_name || 'N/A',
            sortable: true,
            key: 'category_name',
        },
        {
            name: t('Products.Overview.price'),
            selector: row => formatResolvedAmount({
                eurAmount: row.organization_price ?? row.initial_price ?? 0,
                localAmount: row.organization_price_local ?? row.initial_price_local,
                localCurrencyId: row.currency_id,
            }),
            sortable: true,
            key: 'organization_price',
        },
        {
            name: t('Products.Overview.stock'),
            selector: row => row.stock_quantity ?? 0,
            sortable: true,
            key: 'stock_quantity',
        },
        {
            name: t('Products.Overview.availability'),
            selector: row => row.is_available ? t('Products.Overview.available') : t('Products.Overview.unavailable'),
            sortable: true,
            key: 'is_available',
        },
        {
            name: t('Products.Overview.source'),
            selector: row => row.source_type || 'N/A',
        },
        {
            name: t('Products.Overview.actions'),
            cell: row => (
                <ActionButton
                    type="button"
                    onClick={() => navigate(`/products/${row.manufacturer_item_id}`)}
                >
                    <MdVisibility size={20} />
                </ActionButton>
            ),
        },
    ];

    /**
     * Sortiranje se prosljedjuje, ne cita iz stanja: setSortField je asinkron, pa
     * je fetchData nakon njega jos vidio prethodnu vrijednost - prvi klik na
     * stupac nije sortirao nista, a drugi je sortirao po stupcu iz prvog klika.
     */
    const fetchData = useCallback(async (page, size = perPage, appliedFilters = filters, sort = null) => {
        setLoading(true);
        try {
            const response = await fetchCatalogProducts({
                page,
                perPage: size,
                sortBy: sort ? sort.field : sortField,
                sortDirection: sort ? sort.direction : sortDirection,
                search: appliedFilters.searchTerm,
                manufacturerId: appliedFilters.manufacturerId,
                categoryId: appliedFilters.categoryId,
            });
            setProducts(response.data || []);
            setTotalRows(response.total || 0);
        } catch (error) {
            console.error('Error fetching catalog products:', error);
        } finally {
            setLoading(false);
        }
    }, [perPage, filters, sortField, sortDirection]);

    // Prvi popis se dohvaca odmah. Prazna tablica koja ceka da netko pritisne
    // pretragu izgleda kao da trgovac nema nijedan proizvod.
    useEffect(() => {
        fetchData(1);
    }, []);

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1);
        fetchData(1, perPage, newFilters);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchData(page);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        fetchData(page, newPerPage);
    };

    const handleSort = (column, direction) => {
        setSortField(column.key);
        setSortDirection(direction);
        fetchData(currentPage, perPage, filters, { field: column.key, direction });
    };

    return (
        <ProductsOverviewContainer>
            <Header>
                <h4>{t('Products.Overview.title')}</h4>
            </Header>

            <ProductsSearchFilters onFilter={handleFilterChange} />

            <DataTable
                columns={columns}
                data={products}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                onSort={handleSort}
                sortServer
                highlightOnHover
                progressPending={loading}
                progressComponent={<StyledSpinner />}
            />
        </ProductsOverviewContainer>
    );
};

export default ProductsOverview;

const ProductsOverviewContainer = styled.div`
    padding: 1.25rem;
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;

    h4 {
        color: rgba(130, 22, 146, 1);
        font-weight: bold;
    }
`;

const StyledSpinner = styled(Spinner)`
    color: rgba(130, 22, 146, 1);
`;

const ActionButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: rgba(130, 22, 146, 1);

    &:hover {
        color: rgba(130, 22, 146, 0.8);
    }
`;
